/**
 * Signals the ECU does not report but that we can compute.
 *
 * This is where most of the driving-insight value lives: real fuel flow, true
 * boost, estimated gear, torque and power, a calibrated accelerator position,
 * GPS-corrected road speed, and the rate-of-change channels that make
 * aggressive driving visible.
 *
 * Everything degrades gracefully. Each derived value states which inputs it
 * found, and `fuel_source` reports exactly how consumption was obtained so a
 * rough estimate is never mistaken for a measurement.
 */
import { FUEL_CONSTANTS, type FuelType, type SignalDef, type VehicleProfile } from '../types.js';
import type { SignalStore } from './store.js';
import { GEAR_NEUTRAL, GEAR_UNKNOWN, GearEstimator, type LearnedGear } from './gear.js';
import { AcceleratorCalibrator, type PedalCalibration } from './pedal.js';
import { GpsTracker, type GpsFix, type SpeedCalibration } from './gps.js';
import { sanitizeCalibration, type FuelCalibration } from './fuel.js';
import {
  JOURNEY_SCOPES, JourneyRecorder,
  type JourneyScope, type JourneyState, type JourneySummary, type JourneyTotals, type TripRecord,
} from './journey.js';

const S = (i: Partial<SignalDef> & Pick<SignalDef, 'key' | 'name' | 'short' | 'unit' | 'category'>): SignalDef => ({
  min: 0,
  max: 100,
  decimals: 1,
  betterWhen: 'neutral',
  ...i,
});

/** How the current fuel-flow figure was arrived at, worst to best. */
export const FUEL_SOURCE_LABELS: Record<number, string> = {
  0: 'None',
  1: 'ECU fuel rate (PID 5E)',
  2: 'ECU mass fuel rate (PID 9D)',
  3: 'Cylinder fuel rate (PID A2)',
  4: 'MAF + measured lambda',
  5: 'MAF + modelled lambda',
  6: 'Speed-density + modelled lambda',
};

export const DERIVED_SIGNALS: SignalDef[] = [
  S({
    key: 'boost', name: 'Boost Pressure', short: 'Boost', unit: 'kPa', category: 'turbo',
    min: -100, max: 200, decimals: 1,
    description: 'Manifold pressure above ambient. Negative values are vacuum (off boost).',
  }),
  S({
    key: 'boost_bar', name: 'Boost Pressure (bar)', short: 'Boost', unit: 'bar', category: 'turbo',
    min: -1, max: 2, decimals: 2,
  }),
  S({
    key: 'maf_effective', name: 'Air Flow (effective)', short: 'Air flow', unit: 'g/s', category: 'air',
    min: 0, max: 250, decimals: 2,
    description: 'Measured MAF where available, otherwise estimated from MAP, RPM and intake temperature.',
  }),
  S({
    key: 'fuel_rate', name: 'Fuel Flow', short: 'Fuel flow', unit: 'L/h', category: 'fuel',
    min: 0, max: 40, decimals: 2, betterWhen: 'lower',
  }),
  S({
    key: 'fuel_source', name: 'Fuel Flow Source', short: 'Fuel src', unit: '', category: 'fuel',
    min: 0, max: 6, decimals: 0, enumLabels: FUEL_SOURCE_LABELS,
    description: 'Which method produced the current fuel figure. Lower numbers are more trustworthy.',
  }),
  S({
    key: 'consumption', name: 'Instant Consumption', short: 'L/100km', unit: 'L/100km', category: 'fuel',
    min: 0, max: 30, decimals: 1, betterWhen: 'lower', warnAbove: 12,
    description: 'Undefined below walking pace, where litres per hour is the meaningful figure.',
  }),
  S({
    key: 'consumption_avg', name: 'Trip Average Consumption', short: 'Avg L/100km', unit: 'L/100km', category: 'trip',
    min: 0, max: 20, decimals: 2, betterWhen: 'lower',
  }),
  S({
    key: 'air_fuel_ratio', name: 'Air/Fuel Ratio', short: 'AFR', unit: ':1', category: 'fuel',
    min: 10, max: 20, decimals: 2,
  }),
  S({
    key: 'accelerator', name: 'Accelerator Position', short: 'Accelerator', unit: '%', category: 'engine',
    min: 0, max: 100, decimals: 0,
    description: '0% with your foot off, 100% floored. Taken from the best pedal or throttle PID the car '
      + "reports, with that sensor's resting offset removed -- the raw PIDs typically read 15-30% at rest. "
      + 'Calibration is learned as you drive and can be set by hand in Setup.',
  }),
  S({
    key: 'road_speed', name: 'Road Speed', short: 'Speed', unit: 'km/h', category: 'vehicle',
    min: 0, max: 240, decimals: 0,
    description: 'Vehicle speed, corrected by the scale factor learned from GPS once there is enough of it. '
      + 'Falls back to GPS alone when the car is not reporting speed.',
  }),
  S({
    key: 'gps_speed', name: 'GPS Speed', short: 'GPS speed', unit: 'km/h', category: 'vehicle',
    min: 0, max: 240, decimals: 1,
    description: 'Ground speed from the phone. About one reading a second, and none without a fix.',
  }),
  S({
    key: 'gps_accuracy', name: 'GPS Accuracy', short: 'GPS ±', unit: 'm', category: 'vehicle',
    min: 0, max: 50, decimals: 0, betterWhen: 'lower', warnAbove: 25,
    description: 'Radius the phone is confident the position lies within. Fixes worse than 25 m are not used.',
  }),
  S({
    key: 'speed_sensor_error', name: 'Speed Sensor Error', short: 'Speed error', unit: '%', category: 'vehicle',
    min: -10, max: 10, decimals: 1,
    description: "How far the car's speed reading is from GPS ground speed; positive means it reads high. "
      + 'Learned only from steady driving above 30 km/h with a good fix.',
  }),
  S({
    key: 'fuel_correction', name: 'Fuel Estimate Correction', short: 'Fuel corr.', unit: '%', category: 'fuel',
    min: -30, max: 30, decimals: 1,
    description: 'How much is added to the estimated fuel flow to match what the pump actually delivered, '
      + 'learned from full-to-full fill-ups. Positive means the car really burns more than the readings suggest.',
  }),
  S({
    key: 'accel_long', name: 'Longitudinal Acceleration', short: 'Accel', unit: 'm/s²', category: 'vehicle',
    min: -6, max: 6, decimals: 2,
    description: 'Differentiated from vehicle speed, so it lags a real accelerometer slightly.',
  }),
  S({
    key: 'accel_g', name: 'Longitudinal G', short: 'G', unit: 'g', category: 'vehicle',
    min: -0.6, max: 0.6, decimals: 3,
  }),
  S({
    key: 'gear', name: 'Estimated Gear', short: 'Gear', unit: '', category: 'vehicle',
    min: 0, max: 8, decimals: 0,
    description: 'Learned from the engine-to-road speed ratio as you drive, and never higher than the number '
      + 'of gears set in Setup. N is stationary, neutral or clutch down; – is moving in a gear that cannot be '
      + 'identified yet.',
    enumLabels: { [GEAR_NEUTRAL]: 'N', [GEAR_UNKNOWN]: '–' },
  }),
  S({
    key: 'gear_ratio', name: 'RPM per km/h', short: 'Ratio', unit: 'rpm/kph', category: 'vehicle',
    min: 0, max: 200, decimals: 1,
  }),
  S({
    key: 'torque_nm', name: 'Engine Torque', short: 'Torque', unit: 'Nm', category: 'engine',
    min: 0, max: 500, decimals: 0,
  }),
  S({
    key: 'power_kw', name: 'Engine Power', short: 'Power', unit: 'kW', category: 'engine',
    min: 0, max: 300, decimals: 1,
  }),
  S({
    key: 'power_hp', name: 'Engine Power (hp)', short: 'Power', unit: 'hp', category: 'engine',
    min: 0, max: 400, decimals: 0,
  }),
  S({
    key: 'throttle_rate', name: 'Throttle Rate of Change', short: 'dThrottle', unit: '%/s', category: 'engine',
    min: -300, max: 300, decimals: 0,
    description: 'How fast your foot is moving. Sharp spikes are what makes a ride feel jerky.',
  }),
  S({
    key: 'rpm_rate', name: 'Engine Speed Rate of Change', short: 'dRPM', unit: 'rpm/s', category: 'engine',
    min: -3000, max: 3000, decimals: 0,
  }),
  S({
    key: 'engine_load_pct', name: 'Load (best available)', short: 'Load', unit: '%', category: 'engine',
    min: 0, max: 100, decimals: 1,
  }),
  S({
    key: 'coolant_warm', name: 'Engine Warmed Up', short: 'Warm', unit: '', category: 'temperature',
    min: 0, max: 1, decimals: 0, enumLabels: { 0: 'Warming up', 1: 'At temperature' },
  }),
  S({
    key: 'fuel_remaining', name: 'Fuel Remaining', short: 'Fuel left', unit: 'L', category: 'fuel',
    min: 0, max: 80, decimals: 1, betterWhen: 'higher',
  }),
  S({
    key: 'range_km', name: 'Estimated Range', short: 'Range', unit: 'km', category: 'fuel',
    min: 0, max: 1200, decimals: 0, betterWhen: 'higher',
  }),
  // ------------------------------------------------------------------- trip
  S({
    key: 'trip_distance', name: 'Trip Distance', short: 'Distance', unit: 'km', category: 'trip', min: 0, max: 1000, decimals: 2,
    description: 'Integrated from road speed, so it carries the GPS speed correction once that is learned.',
  }),
  S({
    key: 'trip_distance_gps', name: 'Trip Distance (GPS)', short: 'GPS distance', unit: 'km', category: 'trip',
    min: 0, max: 1000, decimals: 2,
    description: 'Length of the GPS track alone. A cross-check: it drops out in tunnels and car parks.',
  }),
  S({ key: 'trip_fuel', name: 'Trip Fuel Used', short: 'Fuel used', unit: 'L', category: 'trip', min: 0, max: 80, decimals: 3, betterWhen: 'lower' }),
  S({ key: 'trip_duration', name: 'Trip Duration', short: 'Duration', unit: 's', category: 'trip', min: 0, max: 36000, decimals: 0 }),
  S({ key: 'trip_avg_speed', name: 'Trip Average Speed', short: 'Avg speed', unit: 'km/h', category: 'trip', min: 0, max: 160, decimals: 1 }),
  S({ key: 'trip_idle_pct', name: 'Time Spent Idling', short: 'Idle', unit: '%', category: 'trip', min: 0, max: 100, decimals: 1, betterWhen: 'lower' }),
  S({ key: 'trip_coast_pct', name: 'Time Spent Coasting', short: 'Coasting', unit: '%', category: 'trip', min: 0, max: 100, decimals: 1, betterWhen: 'higher' }),
];

/**
 * How far back the acceleration regression looks. Long enough to swamp the
 * 1 km/h quantisation step, short enough to still catch a real hard stop.
 */
const ACCEL_WINDOW_S = 1.0;

/** Ordinary least-squares slope of v against t. Undefined if t never varies. */
function leastSquaresSlope(pts: { t: number; v: number }[]): number | undefined {
  const n = pts.length;
  let sumT = 0;
  let sumV = 0;
  for (const p of pts) {
    sumT += p.t;
    sumV += p.v;
  }
  const meanT = sumT / n;
  const meanV = sumV / n;
  let num = 0;
  let den = 0;
  for (const p of pts) {
    const dt = p.t - meanT;
    num += dt * (p.v - meanV);
    den += dt * dt;
  }
  return den > 1e-6 ? num / den : undefined;
}

const isFresh = (t: number, now: number): boolean => t > 0 && now - t < SPEED_STALE_MS;

/**
 * Everything learned about a particular car that is worth keeping between
 * drives: its gear ratios, where its accelerator reads at rest and floored,
 * and how far its speed reading is from GPS.
 */
export interface LearnedState {
  gears?: LearnedGear[];
  accelerator?: PedalCalibration[];
  speed?: SpeedCalibration;
  /** How far the fuel-flow estimate is from the pump, from recorded fill-ups. */
  fuel?: FuelCalibration;
}

export type LearnedPart = keyof LearnedState;

export const LEARNED_PARTS: readonly LearnedPart[] = ['gears', 'accelerator', 'speed', 'fuel'];

/**
 * A learned snapshot reduced to what the engine would actually take from it:
 * malformed entries dropped and unconfirmed gears left out. For anything that
 * did not come from this app's own storage, such as an imported file.
 */
export function sanitizeLearned(state: LearnedState | undefined): LearnedState {
  const gears = new GearEstimator();
  gears.import(state?.gears);
  const accelerator = new AcceleratorCalibrator();
  accelerator.import(state?.accelerator);
  const gps = new GpsTracker();
  gps.importCalibration(state?.speed);
  const clean: LearnedState = {
    gears: gears.export(), accelerator: accelerator.export(), speed: gps.exportCalibration(),
  };
  const fuel = sanitizeCalibration(state?.fuel);
  if (fuel) clean.fuel = fuel;
  return clean;
}

/** The car's speed is only lined up with a GPS fix if a sample is this close in time. */
const GPS_ALIGN_MS = 1000;
/**
 * A speed reading older than this is not the car's speed now. The session
 * polls speed at least once a second, so this only trips when the car has
 * stopped answering -- and then integrating the last value would keep adding
 * distance to a stationary car.
 */
const SPEED_STALE_MS = 3000;

export class DerivedEngine {
  readonly gearEstimator: GearEstimator;
  readonly accelerator = new AcceleratorCalibrator();
  readonly gps = new GpsTracker();
  readonly journey = new JourneyRecorder();
  private fuelCalibration: FuelCalibration | undefined;
  private lastTime = 0;
  private prevAccelerator: number | undefined;
  private prevRpm: number | undefined;
  /** Recent (seconds, m/s) pairs used to regress acceleration. */
  private speedWindow: { t: number; v: number }[] = [];
  /** Time of the last speed reading handed to the gear estimator. */
  private gearFedAt = 0;

  constructor(private readonly store: SignalStore, private vehicle: VehicleProfile) {
    for (const def of DERIVED_SIGNALS) store.register(def);
    this.gearEstimator = new GearEstimator(vehicle.gearCount, vehicle.transmission);
  }

  setVehicle(v: VehicleProfile): void {
    this.vehicle = v;
    this.gearEstimator.setGearCount(v.gearCount);
    this.gearEstimator.setTransmission(v.transmission);
  }

  resetTrip(): void {
    this.journey.reset('trip');
    this.gps.resetTrip();
  }

  /** Start a span over: 'tank' after a fill-up, 'life' to wipe the totals. */
  resetScope(scope: JourneyScope, now = Date.now()): void {
    this.journey.reset(scope, now);
    if (scope === 'trip') this.gps.resetTrip();
  }

  summary(scope: JourneyScope): JourneySummary {
    return this.journey.summary(scope);
  }

  totals(scope: JourneyScope): Readonly<JourneyTotals> {
    return this.journey.totals(scope);
  }

  /** What has been counted so far, to be stored and handed back next time. */
  exportJourney(): JourneyState {
    return this.journey.export();
  }

  importJourney(state: JourneyState | undefined): void {
    this.journey.import(state);
  }

  /** Called when a drive ends, so it can be added to the log. */
  set onTripComplete(fn: ((trip: TripRecord) => void) | undefined) {
    this.journey.onTripComplete = fn;
  }

  exportLearned(): LearnedState {
    const out: LearnedState = {
      gears: this.gearEstimator.export(),
      accelerator: this.accelerator.export(),
      speed: this.gps.exportCalibration(),
    };
    if (this.fuelCalibration) out.fuel = { ...this.fuelCalibration };
    return out;
  }

  importLearned(state: LearnedState | undefined): void {
    this.gearEstimator.import(state?.gears);
    this.accelerator.import(state?.accelerator);
    this.gps.importCalibration(state?.speed);
    this.fuelCalibration = sanitizeCalibration(state?.fuel);
  }

  /** Apply what the fill-ups say about the fuel estimate. */
  setFuelCalibration(cal: FuelCalibration | undefined): void {
    this.fuelCalibration = cal ? sanitizeCalibration(cal) : undefined;
  }

  get fuelFactor(): number {
    return this.fuelCalibration?.factor ?? 1;
  }

  /** Forget one kind of learned knowledge, e.g. the speed correction after fitting new tyres. */
  resetLearned(part: LearnedPart): void {
    if (part === 'gears') this.gearEstimator.reset();
    else if (part === 'accelerator') this.accelerator.reset();
    else if (part === 'fuel') this.fuelCalibration = undefined;
    else this.gps.resetCalibration();
  }

  /**
   * Take in a position fix from the phone. Works whether or not the adapter
   * is connected; without it, GPS speed stands in for road speed.
   * `carConnected` false means the car's last readings are history, however
   * recent.
   */
  ingestGps(fix: GpsFix, now = Date.now(), carConnected = true): void {
    const st = this.store;
    const speed = st.get('speed');
    const obdFresh = carConnected && speed !== undefined && isFresh(speed.stats.lastUpdate, now);
    const obdKph = obdFresh ? speed.history.valueNear(fix.t, GPS_ALIGN_MS) : undefined;

    this.gps.ingest(fix, { kph: obdKph, accel: obdFresh ? st.value('accel_long') : undefined });

    if (Number.isFinite(fix.accuracyM)) st.update('gps_accuracy', fix.accuracyM, now);
    const gpsKph = this.gps.speedKph;
    if (gpsKph !== undefined) st.update('gps_speed', gpsKph, now);
    st.update('trip_distance_gps', this.gps.distanceM / 1000, now);
    const factor = this.gps.factor;
    if (factor !== undefined) st.update('speed_sensor_error', (1 / factor - 1) * 100, now);
    if (!obdFresh && gpsKph !== undefined) st.update('road_speed', gpsKph, now);
  }

  get tripSnapshot(): Readonly<JourneyTotals> {
    return this.journey.totals('trip');
  }

  /**
   * Recompute every derived signal. Call once per polling batch, after the raw
   * values have landed but before `commit()`.
   */
  compute(now = Date.now()): void {
    const st = this.store;
    const dt = this.lastTime === 0 ? 0 : Math.min((now - this.lastTime) / 1000, 2);
    this.lastTime = now;

    const rpm = st.value('rpm');
    // The car's own reading, which the gear and acceleration maths need
    // unscaled, and road speed for everything measuring the journey: the car's
    // reading corrected by GPS, or GPS alone if the car has gone quiet.
    const speedState = st.get('speed');
    const rawSpeed = speedState && isFresh(speedState.stats.lastUpdate, now) ? speedState.value : undefined;
    const factor = this.gps.factor ?? 1;
    const gpsSpeed = this.gps.last && isFresh(this.gps.last.t, now) ? this.gps.speedKph : undefined;
    const speed = rawSpeed !== undefined ? rawSpeed * factor : gpsSpeed;
    const map = st.value('map');
    const baro = st.value('baro');
    const iat = st.value('intake_temp');
    const maf = st.value('maf');
    const coolant = st.value('coolant_temp');
    const fuel = this.vehicle.fuelType;
    const k = FUEL_CONSTANTS[fuel] ?? FUEL_CONSTANTS.unknown;

    if (speed !== undefined) st.update('road_speed', speed, now);

    // ------------------------------------------------------------- pressure
    if (map !== undefined) {
      const ambient = baro ?? 101;
      st.update('boost', map - ambient, now);
      st.update('boost_bar', (map - ambient) / 100, now);
    }

    // -------------------------------------------------------------- airflow
    let airflow = maf;
    let usedSpeedDensity = false;
    if (airflow === undefined && map !== undefined && rpm !== undefined) {
      airflow = this.speedDensityMaf(rpm, map, iat ?? 25);
      usedSpeedDensity = true;
    }
    if (airflow !== undefined) st.update('maf_effective', airflow, now);

    // ------------------------------------------------------------ fuel flow
    const flow = this.fuelFlow(airflow, usedSpeedDensity, rpm, k, fuel);
    const { source, lambda } = flow;
    // Every figure below is an estimate from a sensor; fill-ups say how far off
    // it is, so the correction is applied once, here, at the source.
    const fuelFactor = this.fuelFactor;
    const lph = flow.lph === undefined ? undefined : flow.lph * fuelFactor;
    if (this.fuelCalibration) st.update('fuel_correction', (fuelFactor - 1) * 100, now);
    if (lph !== undefined) {
      st.update('fuel_rate', lph, now);
      st.update('fuel_source', source, now);
      if (speed !== undefined && speed > 5) {
        st.update('consumption', (lph / speed) * 100, now);
      }
    }
    if (lambda !== undefined) st.update('air_fuel_ratio', lambda * k.afr, now);

    // --------------------------------------------------------- acceleration
    //
    // Vehicle speed arrives quantised to whole km/h. Differentiating two
    // consecutive samples 50 ms apart turns a single 1 km/h step into an
    // apparent 5.5 m/s2, which is well past the "harsh braking" threshold --
    // so naive differentiation invents events that never happened. Regressing
    // a least-squares slope across a window instead averages the quantisation
    // out. The cost is roughly half a window of lag, which for a driving-style
    // readout is a fair trade.
    //
    // The regression runs on the car's own reading, so an update to the GPS
    // correction never shows up as a jump; scaling the slope afterwards is exact.
    if (rawSpeed !== undefined) {
      const ts = now / 1000;
      this.speedWindow.push({ t: ts, v: rawSpeed / 3.6 });
      // Keep three points whatever the poll rate. Trimming purely on age drops
      // the window to two samples the moment polling falls below ~2 Hz, and two
      // is one short of regressing anything -- so on a slow or lossy adapter
      // acceleration silently never gets computed at all.
      while (this.speedWindow.length > 3 && ts - (this.speedWindow[0]?.t ?? ts) > ACCEL_WINDOW_S) {
        this.speedWindow.shift();
      }
      if (this.speedWindow.length >= 3) {
        const slope = leastSquaresSlope(this.speedWindow);
        if (slope !== undefined) {
          st.update('accel_long', slope * factor, now);
          st.update('accel_g', (slope * factor) / 9.80665, now);
        }
      }
    }

    // ----------------------------------------------------------- accelerator
    const accelerator = this.accelerator.update(st, rpm, now);
    if (accelerator !== undefined) st.update('accelerator', accelerator, now);

    // ------------------------------------------------------------ rate channels
    if (accelerator !== undefined && dt > 0.02) {
      if (this.prevAccelerator !== undefined) {
        st.update('throttle_rate', (accelerator - this.prevAccelerator) / dt, now);
      }
      this.prevAccelerator = accelerator;
    }
    if (rpm !== undefined && dt > 0.02) {
      if (this.prevRpm !== undefined) st.update('rpm_rate', (rpm - this.prevRpm) / dt, now);
      this.prevRpm = rpm;
    }

    // ------------------------------------------------------------------ gear
    //
    // Each new speed reading is paired with engine speed at that same moment,
    // interpolated from the rpm history. Adapters that poll one PID at a time
    // take the two readings a few hundred milliseconds apart, which under
    // acceleration is enough to skew the ratio by several percent.
    const rpmState = st.get('rpm');
    const speedAt = st.get('speed')?.stats.lastUpdate ?? 0;
    const rpmAt = rpmState?.stats.lastUpdate ?? 0;
    if (rpmState && rawSpeed !== undefined && speedAt > this.gearFedAt && rpmAt >= speedAt) {
      const pairedRpm = rpmState.history.interpolate(speedAt, 600);
      if (pairedRpm !== undefined) {
        this.gearFedAt = speedAt;
        if (rawSpeed > 0) st.update('gear_ratio', pairedRpm / rawSpeed, now);
        const gear = this.gearEstimator.update({
          t: speedAt,
          rpm: pairedRpm,
          speedKph: rawSpeed,
          ...(accelerator !== undefined ? { pedal: accelerator } : {}),
        });
        st.update('gear', gear, now);
      }
    }

    // ------------------------------------------------------- torque & power
    const torquePct = st.value('torque_actual');
    const torqueRef = st.value('torque_reference');
    let torqueNm: number | undefined;
    if (torquePct !== undefined && torqueRef !== undefined && torqueRef > 0) {
      torqueNm = (torquePct / 100) * torqueRef;
    }
    if (torqueNm !== undefined && rpm !== undefined) {
      st.update('torque_nm', torqueNm, now);
      const kw = (torqueNm * rpm) / 9549;
      st.update('power_kw', kw, now);
      st.update('power_hp', kw * 1.34102, now);
    } else if (lph !== undefined && k.bsfc > 0 && rpm !== undefined && rpm > 700) {
      // No torque PIDs: infer shaft power from fuel burnt and a typical BSFC.
      // Deliberately rough -- it tracks trends, it is not a dyno figure.
      const fuelGPerH = lph * k.densityGPerL;
      const kw = fuelGPerH / k.bsfc;
      st.update('power_kw', kw, now);
      st.update('power_hp', kw * 1.34102, now);
      st.update('torque_nm', (kw * 9549) / rpm, now);
    }

    // ------------------------------------------------------------- misc state
    const load = st.value('absolute_load') ?? st.value('engine_load');
    if (load !== undefined) st.update('engine_load_pct', Math.min(load, 100), now);
    if (coolant !== undefined) st.update('coolant_warm', coolant >= 75 ? 1 : 0, now);

    const level = st.value('fuel_level');
    if (level !== undefined) {
      const litres = (level / 100) * this.vehicle.tankCapacityL;
      st.update('fuel_remaining', litres, now);
      // Since the last fill-up is the better basis; this drive alone is noisy.
      const tank = this.journey.summary('tank');
      const avg = (tank.distanceKm > 3 ? tank.consumption : undefined) ?? this.journey.summary('trip').consumption;
      if (avg !== undefined && avg > 0.1) st.update('range_km', (litres / avg) * 100, now);
    }

    // ------------------------------------------------------------ the totals
    if (dt > 0) {
      this.journey.accumulate(dt, { speedKph: speed, rpm, accelerator, lph }, st, now);
      this.publishTrip(now);
    }
  }

  /** The trip span is the one with signals of its own, for dashboards. */
  private publishTrip(now: number): void {
    const st = this.store;
    const t = this.journey.summary('trip');
    if (t.seconds <= 0) return;
    st.update('trip_distance', t.distanceKm, now);
    st.update('trip_fuel', t.fuelL, now);
    st.update('trip_duration', t.seconds, now);
    if (t.avgSpeedKph !== undefined) st.update('trip_avg_speed', t.avgSpeedKph, now);
    if (t.idlePct !== undefined) st.update('trip_idle_pct', t.idlePct, now);
    if (t.coastPct !== undefined) st.update('trip_coast_pct', t.coastPct, now);
    if (t.consumption !== undefined) st.update('consumption_avg', t.consumption, now);
  }

  /**
   * Speed-density airflow estimate for engines with no MAF sensor, which is
   * most small diesels and quite a few older petrol engines.
   *
   *   m_dot = (rpm / 120) * Vd * VE * MAP / (R * T)
   *
   * with rpm/120 because a four-stroke fills the displacement once per two
   * revolutions. Accuracy hinges entirely on the configured VE.
   */
  private speedDensityMaf(rpm: number, mapKpa: number, iatC: number): number {
    const cyclesPerSec = rpm / 120;
    const volumeM3 = this.vehicle.displacementL / 1000;
    const tempK = iatC + 273.15;
    const densityKgM3 = (mapKpa * 1000) / (287.05 * tempK);
    return cyclesPerSec * volumeM3 * this.vehicle.volumetricEfficiency * densityKgM3 * 1000;
  }

  /**
   * Fuel flow in litres per hour, using the best source available.
   *
   * Preference order matters. A directly reported rate beats anything we can
   * infer, and an inferred figure that used a measured lambda beats one that
   * had to model it.
   */
  private fuelFlow(
    airflow: number | undefined,
    usedSpeedDensity: boolean,
    rpm: number | undefined,
    k: { afr: number; densityGPerL: number },
    fuelType: FuelType,
  ): { lph: number | undefined; source: number; lambda: number | undefined } {
    const st = this.store;

    const ecuRate = st.value('fuel_rate_ecu');
    if (ecuRate !== undefined && ecuRate >= 0) {
      return { lph: ecuRate, source: 1, lambda: this.measuredLambda() };
    }

    const massRate = st.value('fuel_rate_gs');
    if (massRate !== undefined && massRate >= 0) {
      return { lph: (massRate * 3600) / k.densityGPerL, source: 2, lambda: this.measuredLambda() };
    }

    // Diesels frequently expose injected quantity per stroke but no flow rate.
    const cylRate = st.value('cylinder_fuel_rate');
    if (cylRate !== undefined && rpm !== undefined && rpm > 0) {
      const strokesPerSec = (rpm / 60 / 2) * this.vehicle.cylinders;
      const gPerSec = (cylRate * strokesPerSec) / 1000;
      return { lph: (gPerSec * 3600) / k.densityGPerL, source: 3, lambda: this.measuredLambda() };
    }

    if (airflow === undefined) return { lph: undefined, source: 0, lambda: undefined };

    const measured = this.measuredLambda();
    const lambda = measured ?? this.modelledLambda(fuelType);
    const fuelGPerSec = airflow / (lambda * k.afr);
    const lph = (fuelGPerSec * 3600) / k.densityGPerL;
    const source = measured !== undefined ? 4 : usedSpeedDensity ? 6 : 5;
    return { lph, source, lambda };
  }

  /** A real lambda reading from a wideband sensor or the ECU's own target. */
  private measuredLambda(): number | undefined {
    const st = this.store;
    for (const key of ['o2_s1_lambda', 'o2_s1_lambda_i', 'o2_s5_lambda', 'lambda_commanded']) {
      const v = st.value(key);
      if (v !== undefined && v > 0.4 && v < 8) return v;
    }
    return undefined;
  }

  /**
   * Fallback lambda when nothing measures it.
   *
   * A petrol engine runs closed-loop at stoichiometric almost all the time, so
   * 1.0 plus the fuel trims is a good guess. A diesel is always lean and its
   * lambda swings enormously with load -- roughly 5:1 at idle down to about
   * 1.15 at full load -- so it gets modelled from load instead. This is the
   * least trustworthy path, which is why `fuel_source` reports it.
   */
  private modelledLambda(fuelType: FuelType): number {
    const st = this.store;
    if (fuelType === 'diesel') {
      const load = st.value('absolute_load') ?? st.value('engine_load') ?? 30;
      const clamped = Math.max(0, Math.min(100, load));
      return Math.max(1.15, 1.15 + (1 - clamped / 100) * 2.6);
    }
    const stft = st.value('stft_b1') ?? 0;
    const ltft = st.value('ltft_b1') ?? 0;
    // Positive trim means the ECU is adding fuel, i.e. running richer than the
    // uncorrected mixture would be, so lambda moves down.
    return 1 / (1 + (stft + ltft) / 100);
  }
}
